import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CATEGORY_CONFIG, EVENT_CONFIG, NotificationCategory, NotificationEventName, NotificationStatus
} from './notification-events';
import { NotificationEnvelope, NotificationService } from './notification.service';

export interface AppNotification {
  id: string;
  jobKey: string;
  event: NotificationEventName;
  category: NotificationCategory;
  status: NotificationStatus;
  icon: string;
  titleKey: string;
  titleDefault: string;
  message: string;
  link: string;
  createdAt: string;
  read: boolean;
}

const MAX_ITEMS = 50;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const STORAGE_PREFIX = 'aurora.notifications.';

/**
 * The list shown in the header bell. The API has no history endpoint, so notifications
 * are kept per user in localStorage.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationStoreService {
  private hub = inject(NotificationService);
  private oidcAuthService = inject(OidcAuthService);
  private snackbarService = inject(SnackbarService);
  private translate = inject(TranslateService);

  private storageKey: string | null = null;
  private idCounter = 0;

  readonly notifications = signal<AppNotification[]>([]);
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);

  constructor() {
    this.load();
    this.hub.events$.subscribe((e) => this.add(e));
  }

  markRead(id: string) {
    this.update((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  markAllRead() {
    this.update((list) => list.map((n) => (n.read ? n : { ...n, read: true })));
  }

  remove(id: string) {
    this.update((list) => list.filter((n) => n.id !== id));
  }

  clear() {
    this.update(() => []);
  }

  private add(envelope: NotificationEnvelope) {
    const config = EVENT_CONFIG[envelope.event];
    if (!config) return;

    const category = CATEGORY_CONFIG[config.category];
    const payload: any = envelope.payload;
    const notification: AppNotification = {
      id: `${envelope.receivedAt.getTime()}-${++this.idCounter}`,
      jobKey: category.jobKey(payload),
      event: envelope.event,
      category: config.category,
      status: config.status,
      icon: category.icon,
      titleKey: config.titleKey,
      titleDefault: config.titleDefault,
      message: category.message
        ? category.message(payload, (key, fallback) => this.translate.instant(key, { Default: fallback }))
        : payload.error ?? payload.message ?? '',
      link: category.link(payload),
      createdAt: envelope.receivedAt.toISOString(),
      read: false
    };

    // A finished/failed event replaces the "started" row for the same job,
    // and an edited request replaces the row still waiting on the user.
    this.update((list) => [
      notification,
      ...list.filter((n) => !(n.jobKey === notification.jobKey && (n.status === 'info' || n.status === 'action')))
    ]);

    if (config.toast) {
      const title = this.translate.instant(config.titleKey, { Default: config.titleDefault });
      const text = notification.message ? `${title}: ${notification.message}` : title;
      this.snackbarService.openSnackBar(text, 'clear', config.toastType ?? config.status);
    }
  }

  private update(fn: (list: AppNotification[]) => AppNotification[]) {
    this.ensureUserScope();
    this.notifications.update((list) => fn(list).slice(0, MAX_ITEMS));
    this.save();
  }

  /** Reload when the signed-in user changes (or wasn't known yet at construction). */
  private ensureUserScope() {
    const key = this.currentStorageKey();
    if (key !== this.storageKey) this.load();
  }

  private currentStorageKey(): string | null {
    const userId = this.oidcAuthService.userProfile?.userId;
    return userId ? `${STORAGE_PREFIX}${userId}` : null;
  }

  private load() {
    this.storageKey = this.currentStorageKey();
    let items: AppNotification[] = [];
    try {
      const raw = this.storageKey ? localStorage.getItem(this.storageKey) : null;
      items = raw ? JSON.parse(raw) : [];
    } catch {
      items = [];
    }
    const cutoff = Date.now() - MAX_AGE_MS;
    this.notifications.set(
      (Array.isArray(items) ? items : []).filter((n) => new Date(n.createdAt).getTime() > cutoff).slice(0, MAX_ITEMS)
    );
  }

  private save() {
    if (!this.storageKey) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.notifications()));
    } catch {
      // Storage full or blocked; the in-memory list still works.
    }
  }
}
